import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react'
import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SessionTimeRange } from '@/components/SessionClockTime'
import { formatAgendaDayDate, formatGuestDateTime } from '@/lib/agendaDayDate'
import { sanitizeRichTextHtml } from '@/lib/sanitizeRichTextHtml'
import {
  Search,
  ArrowLeft,
  Check,
  X,
  Pencil,
  MoreHorizontal,
  FileDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
} from 'lucide-react'

const STATUS_TYPES = ['all', 'pending', 'approved', 'rejected'] as const

const FIELD_MAP: Record<string, { key: keyof GuestRow; tKey: string }> = {
  Title: { key: 'title', tKey: 'title' },
  'First name': { key: 'firstName', tKey: 'firstName' },
  'Last name': { key: 'lastName', tKey: 'lastName' },
  'Phone number': { key: 'phoneNumber', tKey: 'phoneNumber' },
  Email: { key: 'email', tKey: 'email' },
  Organization: { key: 'organization', tKey: 'organization' },
  Position: { key: 'position', tKey: 'position' },
  Gender: { key: 'gender', tKey: 'gender' },
  Country: { key: 'country', tKey: 'country' },
  City: { key: 'city', tKey: 'city' },
  Age: { key: 'age', tKey: 'age' },
  'Id number': { key: 'idNumber', tKey: 'idNumber' },
  'Passport number': { key: 'passportNumber', tKey: 'passportNumber' },
  'Blood type': { key: 'bloodType', tKey: 'bloodType' },
}

interface SelectedSession {
  id: number
  dayNumber: number | null
  dayLabel?: string | null
  dayDate?: string | null
  time: string
  endTime?: string
  description: string
}

interface GuestRow {
  id: number
  token: string
  createdAt: string | null
  firstName: string | null
  lastName: string | null
  phoneNumber: string | null
  email: string | null
  organization: string | null
  position: string | null
  gender: string | null
  country: string | null
  city: string | null
  age: number | string | null
  title: string | null
  idNumber: string | null
  passportNumber: string | null
  bloodType: string | null
  userType: string | null
  approved: boolean | null
  printed: boolean | null
  checkedIn?: boolean
  imageUrl: string | null
  customFields: Record<string, string>
  selectedSessions?: SelectedSession[]
}

interface Pagination {
  currentPage: number
  perPage: number
  totalCount: number
  totalPages: number
}

interface ListFilters {
  type: string
  search: string
  sort: string
  direction: string
  userType?: string | null
  dayItemId?: string | number | null
}

interface Props extends Omit<AppShellProps, 'children'> {
  event: { id: number; name: string; eventDate?: string | null }
  registrationFields: string[]
  customFieldNames: string[]
  eventUsers: GuestRow[]
  eventUserTypes?: string[]
  filter: string | null
  filters: ListFilters
  pagination: Pagination
  permissions?: { canApprove?: boolean; canEdit?: boolean }
  urls: AppShellProps['urls'] & {
    details: string
    editAdmin: string
    eventDashboard: string
    approve: string
    reject: string
    bulkApprove: string
    bulkReject: string
    exportCsv: string
  }
  translations: AppShellProps['translations'] & {
    registeredUsers: string
    for_: string
    registerDate: string
    firstName: string
    lastName: string
    phoneNumber: string
    email: string
    organization: string
    position: string
    gender: string
    country: string
    city: string
    age: string
    title: string
    idNumber: string
    passportNumber: string
    bloodType: string
    image: string
    userType: string
    approved: string
    view: string
    back: string
    true_: string
    false_: string
    pending: string
    approve: string
    reject: string
    actions: string
    userDetails: string
    name: string
    rejected: string
    guest?: string
    contact?: string
    edit?: string
    search?: string
    close?: string
    all?: string
    clearFilters?: string
    noResults?: string
    checkedIn?: string
    notCheckedIn?: string
    exportCsv?: string
    rowsPerPage?: string
    showing?: string
    of?: string
    users?: string
    registrations?: string
    selectedCount?: string
    selectedSessions?: string
    dayLabel?: string
    id?: string
  }
}

function blankToNull(value: unknown) {
  if (value == null) return null
  const text = String(value).trim()
  return text === '' ? null : text
}

export default function List(props: Props) {
  const {
    event,
    registrationFields,
    customFieldNames,
    eventUsers,
    eventUserTypes = [],
    filter,
    filters: incomingFilters,
    pagination: incomingPagination,
    permissions,
    urls,
    translations: t,
    locale,
    ...shell
  } = props

  const isRtl = locale === 'ar'
  const canApprove = permissions?.canApprove !== false
  const canEdit = permissions?.canEdit !== false
  const filters: ListFilters = incomingFilters || { type: 'all', search: '', sort: 'created_at', direction: 'desc' }
  const pagination: Pagination = incomingPagination || {
    currentPage: 1,
    perPage: eventUsers.length || 25,
    totalCount: eventUsers.length,
    totalPages: 1,
  }
  const [searchInput, setSearchInput] = useState(filters.search || '')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [detailUser, setDetailUser] = useState<GuestRow | null>(null)
  const searchTimer = useRef<number | null>(null)

  useEffect(() => {
    setSearchInput(filters.search || '')
  }, [filters.search])

  useEffect(() => {
    setSelected(new Set())
  }, [pagination.currentPage, filters.type, filters.search, filters.userType, filters.dayItemId])

  const statusValue = STATUS_TYPES.includes(filters.type as (typeof STATUS_TYPES)[number])
    ? filters.type
    : 'all'

  const hasExtraFilters = Boolean(filters.search || filters.userType || filters.dayItemId || (filters.type && filters.type !== 'all'))

  const navigate = (overrides: Record<string, string | number | undefined | null>) => {
    const params: Record<string, string | number> = {}
    const next = {
      type: filters.type || 'all',
      search: filters.search || '',
      sort: filters.sort || 'created_at',
      direction: filters.direction || 'desc',
      page: pagination.currentPage,
      per_page: pagination.perPage,
      user_type: filters.userType || undefined,
      day_item_id: filters.dayItemId || undefined,
      ...overrides,
    }
    Object.entries(next).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return
      params[key] = value
    })
    router.get(window.location.pathname, params, {
      preserveState: true,
      preserveScroll: true,
      only: ['eventUsers', 'pagination', 'filters', 'filter'],
    })
  }

  const onSearchChange = (value: string) => {
    setSearchInput(value)
    if (searchTimer.current) window.clearTimeout(searchTimer.current)
    searchTimer.current = window.setTimeout(() => {
      navigate({ search: value, page: 1 })
    }, 400)
  }

  const exportHref = useMemo(() => {
    const params = new URLSearchParams()
    if (filters.type) params.set('type', filters.type)
    if (filters.search) params.set('search', filters.search)
    if (filters.sort) params.set('sort', filters.sort)
    if (filters.direction) params.set('direction', filters.direction)
    if (filters.userType) params.set('user_type', String(filters.userType))
    if (filters.dayItemId) params.set('day_item_id', String(filters.dayItemId))
    const query = params.toString()
    return query ? `${urls.exportCsv}?${query}` : urls.exportCsv
  }, [filters, urls.exportCsv])

  const fullName = (eu: GuestRow) =>
    [eu.title, eu.firstName, eu.lastName].filter(Boolean).join(' ') || '—'

  const showApproved = (val: boolean | null) => {
    if (val === true) return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">{t.approved}</Badge>
    if (val === false) return <Badge variant="destructive">{t.rejected || t.false_}</Badge>
    return <Badge variant="outline" className="text-[#91918E]">{t.pending}</Badge>
  }

  const approveUser = (eu: GuestRow, event?: MouseEvent) => {
    event?.stopPropagation()
    router.get(urls.approve, { id: eu.token, type: filters.type }, { preserveScroll: true })
  }

  const rejectUser = (eu: GuestRow, event?: MouseEvent) => {
    event?.stopPropagation()
    if (!window.confirm(`${t.reject || 'Reject'} ${fullName(eu)}?`)) return false
    router.get(urls.reject, { id: eu.token, type: filters.type }, { preserveScroll: true })
    return true
  }

  const editUser = (eu: GuestRow, event?: MouseEvent) => {
    event?.stopPropagation()
    router.get(urls.editAdmin, { id: eu.token })
  }

  const visibleDetailFields = (eu: GuestRow) => {
    const standard = registrationFields
      .filter((field) => FIELD_MAP[field])
      .map((field) => ({
        label: (t as Record<string, string>)[FIELD_MAP[field].tKey] ?? field,
        value: blankToNull(eu[FIELD_MAP[field].key]),
        ltr: FIELD_MAP[field].key === 'phoneNumber' || FIELD_MAP[field].key === 'email',
      }))
      .filter((row) => row.value)

    const custom = customFieldNames
      .map((name) => ({
        label: name,
        value: blankToNull(eu.customFields[name] ?? eu.customFields[name.replace(/\?$/, '')]),
        ltr: false,
      }))
      .filter((row) => row.value)

    return [...standard, ...custom]
  }

  const pageTokens = eventUsers.map((eu) => eu.token)
  const allSelected = pageTokens.length > 0 && pageTokens.every((token) => selected.has(token))
  const toggleAll = (checked: boolean) => {
    setSelected(checked ? new Set(pageTokens) : new Set())
  }
  const toggleOne = (token: string) => {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(token)) next.delete(token)
      else next.add(token)
      return next
    })
  }

  const bulkAction = (url: string) => {
    if (selected.size === 0) return
    router.post(url, { tokens: Array.from(selected), type: filters.type }, { preserveScroll: true })
  }

  const startRow = pagination.totalCount === 0 ? 0 : (pagination.currentPage - 1) * pagination.perPage + 1
  const endRow = Math.min(pagination.currentPage * pagination.perPage, pagination.totalCount)
  const eventDateLabel = formatAgendaDayDate(event.eventDate, locale || 'en')
  const subtitle = [eventDateLabel, `${pagination.totalCount} ${t.registrations || t.users || ''}`.trim()]
    .filter(Boolean)
    .join(' · ')

  const stopRowClick = (event: MouseEvent) => event.stopPropagation()

  return (
    <AppShell {...shell} locale={locale} urls={urls} translations={t}>
      <Head title={`${t.registeredUsers} – ${event.name}`} />

      <div className="mx-auto max-w-[1400px] px-4 py-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-2 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 text-[#91918E] hover:text-[#37352F]"
              onClick={() => router.visit(urls.eventDashboard)}
            >
              <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-xl font-semibold text-[#37352F] truncate">{event.name}</h1>
              <p className="text-sm text-[#91918E]">{subtitle}</p>
            </div>
          </div>
          <a
            href={exportHref}
            className="inline-flex h-9 items-center justify-center rounded-md border border-[#E8E8E5] bg-white px-3 text-sm font-medium text-[#37352F] hover:bg-[#F7F7F5]"
          >
            <FileDown className="h-4 w-4 me-1.5" />
            {t.exportCsv || 'Export'}
          </a>
        </div>

        <div className="mb-3 flex flex-col gap-2 lg:flex-row lg:items-center">
          <div className="relative w-full lg:max-w-sm">
            <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#91918E]" />
            <Input
              value={searchInput}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={`${t.name || t.guest || 'Name'}, ${t.email}, ${t.phoneNumber}…`}
              className="ps-9 border-[#E8E8E5]"
            />
          </div>
          <select
            value={statusValue}
            onChange={(e) => navigate({ type: e.target.value, page: 1 })}
            className="h-9 rounded-md border border-[#E8E8E5] bg-white px-2 text-sm text-[#37352F]"
          >
            <option value="all">{t.all || 'All'}</option>
            <option value="pending">{t.pending}</option>
            <option value="approved">{t.approved}</option>
            <option value="rejected">{t.rejected}</option>
          </select>
          {eventUserTypes.length > 0 && (
            <select
              value={filters.userType || ''}
              onChange={(e) => {
                const nextType = STATUS_TYPES.includes(filters.type as (typeof STATUS_TYPES)[number])
                  ? filters.type
                  : 'all'
                navigate({ type: nextType, user_type: e.target.value || undefined, page: 1 })
              }}
              className="h-9 rounded-md border border-[#E8E8E5] bg-white px-2 text-sm text-[#37352F]"
            >
              <option value="">{t.userType}: {t.all || 'All'}</option>
              {eventUserTypes.map((userType) => (
                <option key={userType} value={userType}>{userType}</option>
              ))}
            </select>
          )}
          {hasExtraFilters && (
            <Button
              variant="ghost"
              className="h-9 text-[#91918E]"
              onClick={() => navigate({ type: 'all', search: '', user_type: undefined, day_item_id: undefined, page: 1 })}
            >
              {t.clearFilters || 'Clear filters'}
            </Button>
          )}
          {filter && filter !== 'all' && filters.type === 'type' && (
            <span className="text-sm text-[#91918E]">{filter}</span>
          )}
        </div>

        {selected.size > 0 && canApprove && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-[#E8E8E5] bg-[#F7F7F5] px-3 py-2">
            <span className="text-sm text-[#37352F]">
              {selected.size} {t.selectedCount || 'selected'}
            </span>
            <Button size="sm" className="h-8 bg-green-600 text-white hover:bg-green-700" onClick={() => bulkAction(urls.bulkApprove)}>
              <Check className="h-3.5 w-3.5 me-1" />
              {t.approve}
            </Button>
            <Button size="sm" variant="destructive" className="h-8" onClick={() => bulkAction(urls.bulkReject)}>
              <X className="h-3.5 w-3.5 me-1" />
              {t.reject}
            </Button>
          </div>
        )}

        <div className="overflow-hidden rounded-lg border border-[#E8E8E5] bg-white">
          <div className="max-h-[calc(100vh-18rem)] overflow-auto">
            <table className="w-full caption-bottom text-sm">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_#E8E8E5]">
                <tr className="text-[#91918E]">
                  <th className="h-10 w-10 px-2 text-start font-medium">
                    <Checkbox checked={allSelected} onCheckedChange={(checked) => toggleAll(Boolean(checked))} />
                  </th>
                  <th className="h-10 px-2 text-start font-medium cursor-pointer" onClick={() => navigate({ sort: 'first_name', direction: filters.sort === 'first_name' && filters.direction === 'asc' ? 'desc' : 'asc', page: 1 })}>
                    <span className="inline-flex items-center">
                      {t.guest || t.name}
                      <ArrowUpDown className="h-3 w-3 ms-1" />
                    </span>
                  </th>
                  <th className="h-10 px-2 text-start font-medium">{t.contact || t.email}</th>
                  <th className="h-10 px-2 text-start font-medium cursor-pointer" onClick={() => navigate({ sort: 'organization', direction: filters.sort === 'organization' && filters.direction === 'asc' ? 'desc' : 'asc', page: 1 })}>
                    <span className="inline-flex items-center">
                      {t.organization}
                      <ArrowUpDown className="h-3 w-3 ms-1" />
                    </span>
                  </th>
                  <th className="h-10 px-2 text-start font-medium">{t.userType}</th>
                  <th className="h-10 px-2 text-start font-medium cursor-pointer whitespace-nowrap" onClick={() => navigate({ sort: 'created_at', direction: filters.sort === 'created_at' && filters.direction === 'asc' ? 'desc' : 'asc', page: 1 })}>
                    <span className="inline-flex items-center">
                      {t.registerDate}
                      <ArrowUpDown className="h-3 w-3 ms-1" />
                    </span>
                  </th>
                  <th className="h-10 px-2 text-start font-medium">{t.approved}</th>
                  <th className="h-10 px-2 text-start font-medium">{t.checkedIn || 'Check-in'}</th>
                  <th className="h-10 px-2 text-end font-medium">{t.actions}</th>
                </tr>
              </thead>
              <tbody>
                {eventUsers.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-10 text-center text-[#91918E]">
                      {t.noResults || 'No results'}
                    </td>
                  </tr>
                ) : (
                  eventUsers.map((eu) => (
                    <tr
                      key={eu.id}
                      className="cursor-pointer border-t border-[#E8E8E5] hover:bg-[#F7F7F5]"
                      onClick={() => setDetailUser(eu)}
                    >
                      <td className="px-2 py-2" onClick={stopRowClick}>
                        <Checkbox
                          checked={selected.has(eu.token)}
                          onCheckedChange={() => toggleOne(eu.token)}
                        />
                      </td>
                      <td className="px-2 py-2 font-medium text-[#37352F]">{fullName(eu)}</td>
                      <td className="px-2 py-2 text-[#37352F]">
                        <div className="flex flex-col gap-0.5">
                          {eu.email ? <span dir="ltr">{eu.email}</span> : null}
                          {eu.phoneNumber ? <span className="text-[#91918E]" dir="ltr">{eu.phoneNumber}</span> : null}
                          {!eu.email && !eu.phoneNumber ? '—' : null}
                        </div>
                      </td>
                      <td className="px-2 py-2">{eu.organization || '—'}</td>
                      <td className="px-2 py-2">{eu.userType || '—'}</td>
                      <td className="px-2 py-2 whitespace-nowrap">{formatGuestDateTime(eu.createdAt, locale || 'en') || '—'}</td>
                      <td className="px-2 py-2">{showApproved(eu.approved)}</td>
                      <td className="px-2 py-2 text-[#91918E]">
                        {eu.checkedIn ? (t.checkedIn || 'Checked in') : (t.notCheckedIn || 'Not checked in')}
                      </td>
                      <td className="px-2 py-2" onClick={stopRowClick}>
                        <div className="flex items-center justify-end gap-1.5">
                          {canApprove && eu.approved !== true && (
                            <Button size="sm" className="h-7 px-2 bg-green-600 text-white hover:bg-green-700" onClick={(e) => approveUser(eu, e)}>
                              <Check className="h-3.5 w-3.5 me-1" />
                              {t.approve}
                            </Button>
                          )}
                          {canApprove && eu.approved == null && (
                            <Button size="sm" variant="destructive" className="h-7 px-2" onClick={(e) => rejectUser(eu, e)}>
                              <X className="h-3.5 w-3.5 me-1" />
                              {t.reject}
                            </Button>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger className="flex items-center justify-center h-7 w-7 rounded-[4px] p-0 hover:bg-[#EDEDEC] text-[#91918E] cursor-pointer outline-none">
                              <MoreHorizontal className="h-4 w-4" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align={isRtl ? 'start' : 'end'}>
                              {canEdit && (
                                <DropdownMenuItem onClick={(e) => editUser(eu, e as unknown as MouseEvent)}>
                                  <Pencil className="h-3.5 w-3.5 me-2" />
                                  {t.edit || 'Edit'}
                                </DropdownMenuItem>
                              )}
                              {canApprove && eu.approved !== true && (
                                <DropdownMenuItem onClick={(e) => approveUser(eu, e as unknown as MouseEvent)}>
                                  <Check className="h-3.5 w-3.5 me-2" />
                                  {t.approve}
                                </DropdownMenuItem>
                              )}
                              {canApprove && eu.approved !== false && (
                                <>
                                  {canEdit && <DropdownMenuSeparator />}
                                  <DropdownMenuItem onClick={(e) => rejectUser(eu, e as unknown as MouseEvent)}>
                                    <X className="h-3.5 w-3.5 me-2" />
                                    {t.reject}
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.totalCount > 0 && (
            <div className="flex flex-col gap-2 border-t border-[#E8E8E5] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 text-[13px] text-[#91918E]">
                {t.showing} {startRow}–{endRow} {t.of} {pagination.totalCount}
                <span className="mx-2 text-[#E8E8E5]">|</span>
                <span>{t.rowsPerPage}</span>
                <select
                  value={pagination.perPage}
                  onChange={(e) => navigate({ per_page: Number(e.target.value), page: 1 })}
                  className="h-7 rounded border border-[#E8E8E5] bg-white px-1.5 text-[13px] text-[#37352F]"
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={pagination.currentPage <= 1} onClick={() => navigate({ page: 1 })}>
                  <ChevronsLeft className="h-4 w-4 rtl:rotate-180" />
                </Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={pagination.currentPage <= 1} onClick={() => navigate({ page: pagination.currentPage - 1 })}>
                  <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
                </Button>
                <span className="px-2 text-[13px] tabular-nums text-[#37352F]">
                  {pagination.currentPage} / {pagination.totalPages || 1}
                </span>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={pagination.currentPage >= pagination.totalPages} onClick={() => navigate({ page: pagination.currentPage + 1 })}>
                  <ChevronRight className="h-4 w-4 rtl:rotate-180" />
                </Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={pagination.currentPage >= pagination.totalPages} onClick={() => navigate({ page: pagination.totalPages })}>
                  <ChevronsRight className="h-4 w-4 rtl:rotate-180" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Sheet open={!!detailUser} onOpenChange={(open) => { if (!open) setDetailUser(null) }}>
        <SheetContent
          side={isRtl ? 'left' : 'right'}
          className="w-full sm:max-w-lg overflow-y-auto"
        >
          {detailUser && (
            <>
              <SheetHeader className="pe-8">
                <SheetTitle className="text-[#37352F]">{fullName(detailUser)}</SheetTitle>
                <SheetDescription className="sr-only">{t.userDetails}</SheetDescription>
                <div className="flex items-center gap-2 pt-1">
                  {showApproved(detailUser.approved)}
                  <span className="text-xs text-[#91918E]">
                    {detailUser.checkedIn ? t.checkedIn : t.notCheckedIn}
                  </span>
                </div>
              </SheetHeader>

              <div className="space-y-4 px-4 pb-2">
                {detailUser.imageUrl && (
                  <img src={detailUser.imageUrl} alt="" className="h-20 w-20 rounded-lg border border-[#E8E8E5] object-cover" />
                )}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-[#91918E]">{t.id || 'ID'}</div>
                    <div className="text-sm text-[#37352F]">{detailUser.id}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-[#91918E]">{t.registerDate}</div>
                    <div className="text-sm text-[#37352F]">{formatGuestDateTime(detailUser.createdAt, locale || 'en') || '—'}</div>
                  </div>
                  {visibleDetailFields(detailUser).map((row) => (
                    <div key={String(row.label)} className="min-w-0">
                      <div className="text-[11px] uppercase tracking-wide text-[#91918E]">{row.label}</div>
                      <div className="text-sm text-[#37352F] break-words" dir={row.ltr ? 'ltr' : undefined}>{row.value as ReactNode}</div>
                    </div>
                  ))}
                </div>

                {(detailUser.selectedSessions || []).length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-[#37352F]">{t.selectedSessions}</div>
                      {detailUser.selectedSessions!.map((session) => {
                        const sessionLocale = locale || 'en'
                        const dayDateLabel = formatAgendaDayDate(session.dayDate, sessionLocale)
                        return (
                          <div key={session.id} className="text-sm text-[#37352F] ps-2 border-s-2 border-[#E8E8E5]">
                            {session.dayNumber != null && (
                              <span className="text-[#91918E] me-2">
                                {(t.dayLabel || 'Day %{number}').replace('%{number}', String(session.dayNumber))}
                              </span>
                            )}
                            {dayDateLabel && <span className="text-[#91918E] me-2">{dayDateLabel}</span>}
                            {(session.time || session.endTime) && (
                              <span className="text-[#91918E] me-2">
                                <SessionTimeRange start={session.time} end={session.endTime} locale={sessionLocale} />
                              </span>
                            )}
                            <span
                              className="[&_p]:inline [&_p]:m-0"
                              dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(session.description) }}
                            />
                          </div>
                        )
                      })}
                    </div>
                  </>
                )}
              </div>

              <SheetFooter className="flex-row flex-wrap justify-end gap-2">
                {canEdit && (
                  <Button variant="outline" className="border-[#E8E8E5]" onClick={() => editUser(detailUser)}>
                    <Pencil className="h-4 w-4 me-1" />
                    {t.edit || 'Edit'}
                  </Button>
                )}
                {canApprove && detailUser.approved !== true && (
                  <Button
                    className="bg-green-600 text-white hover:bg-green-700"
                    onClick={() => {
                      approveUser(detailUser)
                      setDetailUser(null)
                    }}
                  >
                    <Check className="h-4 w-4 me-1" />
                    {t.approve}
                  </Button>
                )}
                {canApprove && detailUser.approved == null && (
                  <Button
                    variant="destructive"
                    onClick={() => {
                      if (rejectUser(detailUser)) setDetailUser(null)
                    }}
                  >
                    <X className="h-4 w-4 me-1" />
                    {t.reject}
                  </Button>
                )}
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>
    </AppShell>
  )
}
